import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/autoplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [fiyatlar, setFiyatlar] = useState([]);
  const [duyurular, setDuyurular] = useState("");
  const [images, setImages] = useState([]);
  const [params, setParams] = useState({
    data_refresh_rate_seconds: 61,
    image_switch_rate_seconds: 8,
    oda_adi: "Cizre Kuyumcular Odası",
    baskan_adi: "Başkan Adı"
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchParams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parametreler-public`);
      setParams(response.data);
    } catch (error) {
      console.error("Error fetching parameters:", error);
    }
  }, []);

  const fetchFiyatlar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fiyatlar`);
      setFiyatlar(response.data);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Error fetching prices:", error);
      setLoading(false);
    }
  }, []);

  const fetchDuyurular = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/duyurular-public`);
      setDuyurular(response.data.combined_text || "");
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  }, []);

  const fetchImages = useCallback(async () => {
    const loadedImages = [];
    for (let i = 0; i < 10; i++) {
      try {
        const response = await axios.get(`${API}/resim/${i}`);
        if (response.data.image_base64) {
          loadedImages.push(`data:image/jpeg;base64,${response.data.image_base64}`);
        }
      } catch (error) {
        break;
      }
    }
    if (loadedImages.length === 0) {
      loadedImages.push(
        "https://images.pexels.com/photos/8442328/pexels-photo-8442328.jpeg",
        "https://images.pexels.com/photos/8442330/pexels-photo-8442330.jpeg"
      );
    }
    setImages(loadedImages);
  }, []);

  useEffect(() => {
    fetchParams();
    fetchFiyatlar();
    fetchDuyurular();
    fetchImages();
  }, [fetchParams, fetchFiyatlar, fetchDuyurular, fetchImages]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchFiyatlar();
      fetchDuyurular();
    }, 61000);
    
    return () => clearInterval(interval);
  }, [fetchFiyatlar, fetchDuyurular]);

  const getStatusIcon = (durum) => {
    switch (durum?.toLowerCase()) {
      case "up":
        return <TrendingUp className="w-5 h-5 text-emerald-500" />;
      case "down":
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getRowClass = (durum) => {
    switch (durum?.toLowerCase()) {
      case "up":
        return "bg-emerald-50/50";
      case "down":
        return "bg-red-50/50";
      default:
        return "";
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Fiyatlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Calculate dynamic font size based on number of items
  const itemCount = fiyatlar.length;
  const getFontSize = () => {
    if (itemCount <= 10) return "text-base";
    if (itemCount <= 14) return "text-sm";
    return "text-xs";
  };

  const getPadding = () => {
    if (itemCount <= 10) return "py-2";
    if (itemCount <= 14) return "py-1.5";
    return "py-1";
  };

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col bg-white">
      {/* Header - Compact */}
      <header className="bg-gradient-to-r from-slate-800 to-slate-900 px-3 lg:px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3">
            <div className="h-10 lg:h-12 bg-white rounded-lg flex items-center justify-center shadow-lg px-1.5 lg:px-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_goldprice-live-4/artifacts/j23gl6kf_images.jpg" 
                alt="CK-DER Logo" 
                className="h-8 lg:h-10 w-auto object-contain"
              />
            </div>
            <div>
              <h1 className="text-sm lg:text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {params.oda_adi.toUpperCase()}
              </h1>
              <p className="text-amber-300 text-[10px] lg:text-xs">
                {params.baskan_adi} - Tavsiye Edilen Fiyat Listesi
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="live-indicator hidden lg:flex">
              <span className="live-dot"></span>
              CANLI
            </div>
            <span className="text-slate-400 text-[10px] lg:text-xs">
              {lastUpdate.toLocaleTimeString('tr-TR')}
            </span>
            <a href="/admin/login" className="p-1.5 lg:p-2 text-slate-400 hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 lg:w-5 lg:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content - Full Height */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left Side - Price Table (Full width on mobile, half on desktop) */}
        <div className="w-full lg:w-1/2 flex flex-col bg-white lg:border-r border-slate-200">
          {/* Table Header */}
          <div className="bg-slate-100 border-b border-slate-200 flex-shrink-0">
            <div className="grid grid-cols-12 gap-1 lg:gap-2 px-2 lg:px-4 py-2">
              <div className="col-span-5 font-semibold text-slate-700 text-xs lg:text-sm">Ürün</div>
              <div className="col-span-3 font-semibold text-slate-700 text-xs lg:text-sm text-right">Alış</div>
              <div className="col-span-3 font-semibold text-slate-700 text-xs lg:text-sm text-right">Satış</div>
              <div className="col-span-1 font-semibold text-slate-700 text-xs lg:text-sm text-center">D</div>
            </div>
          </div>
          
          {/* Table Body - Auto fit */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              {fiyatlar.map((fiyat, index) => (
                <div 
                  key={index} 
                  className={`grid grid-cols-12 gap-1 lg:gap-2 px-2 lg:px-4 ${getPadding()} border-b border-slate-100 items-center flex-1 ${getRowClass(fiyat.durum)}`}
                  style={{ maxHeight: `${100 / itemCount}%` }}
                >
                  <div className={`col-span-5 font-medium text-slate-800 ${getFontSize()} truncate`}>
                    {fiyat.urun_adi}
                  </div>
                  <div className={`col-span-3 mono-numbers font-bold text-slate-900 ${getFontSize()} text-right`}>
                    {fiyat.alis}
                  </div>
                  <div className={`col-span-3 mono-numbers font-bold text-slate-900 ${getFontSize()} text-right`}>
                    {fiyat.satis}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {getStatusIcon(fiyat.durum)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Image (Hidden on mobile, visible on desktop) */}
        <div className="hidden lg:block w-1/2 bg-black">
          {images.length > 0 && (
            <Swiper
              modules={[Autoplay, EffectFade]}
              effect="fade"
              autoplay={{
                delay: params.image_switch_rate_seconds * 1000,
                disableOnInteraction: false,
              }}
              loop={true}
              className="h-full w-full"
            >
              {images.map((img, index) => (
                <SwiperSlide key={index} className="h-full">
                  <img
                    src={img}
                    alt={`Slide ${index + 1}`}
                    className="w-full h-full object-contain bg-black"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </main>

      {/* Price Ticker - Compact */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-1 lg:py-1.5 flex-shrink-0">
        <Marquee gradient={false} speed={60}>
          <div className="flex items-center gap-4 lg:gap-6 px-4">
            {fiyatlar.map((fiyat, index) => (
              <div key={index} className="flex items-center gap-1.5 lg:gap-2 text-white text-xs lg:text-sm">
                <span className="font-medium">{fiyat.urun_adi}</span>
                <span className="mono-numbers font-bold">{fiyat.satis}</span>
                {getStatusIcon(fiyat.durum)}
                <span className="text-amber-200 ml-1 lg:ml-2">|</span>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      {/* Announcement Ticker */}
      {duyurular && (
        <div className="bg-slate-900 py-1 lg:py-1.5 flex-shrink-0">
          <Marquee gradient={false} speed={50}>
            <div className="flex items-center gap-4 px-4">
              <span className="text-amber-200 text-xs lg:text-sm">{duyurular}</span>
              <span className="text-amber-400 mx-4">★</span>
              <span className="text-amber-200 text-xs lg:text-sm">{duyurular}</span>
            </div>
          </Marquee>
        </div>
      )}

      {/* Footer - Minimal */}
      <footer className="bg-slate-800 py-1 px-4 flex-shrink-0">
        <p className="text-slate-500 text-[10px] lg:text-xs text-center">
          © {new Date().getFullYear()} AppSaRRaF - Keyf Teknoloji - 0507 744 90 65
        </p>
      </footer>
    </div>
  );
};

export default HomePage;
